// @djsem/shared — Fan Live Link (49) + Mobile Companion (50).
//
// @see docs/state/agents/claude/prompts/102-repository-hardening-security-memory-oop-di-test-home.md

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "./index";
import { SeqCounter } from "./runtime";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 49 — Fan QR / Live Link / Setlist / Post-Show Landing
// ============================================================================

/** The lifecycle mode a fan-facing live-link page is in. */
export type DJSEMFanPageMode = "pre_show" | "live" | "post_show" | "release_promo" | "epk";
/** Who can view a fan live-link page. */
export type DJSEMFanPageVisibility = "private" | "unlisted" | "public";
/** The kind of call-to-action a fan live-link action button performs. */
export type DJSEMFanActionType =
  | "open_url" | "spotify_follow" | "soundcloud_follow" | "save_release"
  | "mailing_list_signup" | "view_setlist" | "view_recap" | "view_epk" | "custom";

/** A single call-to-action button on a fan live-link page. */
export interface DJSEMFanLiveLinkAction {
  id: string;
  label: string;
  actionType: DJSEMFanActionType;
  url?: string;
  enabled: boolean;
  priority: number;
  warnings: string[];
}

/** A single track's visibility state within a published setlist. */
export interface DJSEMPublishedSetlistTrack {
  index: number;
  title?: string; artist?: string;
  visible: boolean;
  warnings: string[];
}

/** The published (or not-yet-published) setlist attached to a fan live-link page. */
export interface DJSEMPublishedSetlist {
  enabled: boolean;
  publishTiming: "never" | "during_show" | "after_show" | "manual";
  trackItems: DJSEMPublishedSetlistTrack[];
}

/** Fan email-collection consent configuration for a live-link page. */
export interface DJSEMFanConsentConfig {
  collectEmail: boolean;
  consentText?: string;
  privacyUrl?: string;
}

/** A fan-facing QR/live-link page: actions, setlist, consent config, and visibility state. */
export interface DJSEMFanLiveLinkPage {
  id: string;
  projectId?: string;
  slug: string;
  title: string;
  status: "draft" | "active" | "paused" | "post_show" | "archived";
  visibility: DJSEMFanPageVisibility;
  pageMode: DJSEMFanPageMode;
  artistName: string;
  eventTitle?: string;
  heroAssetId?: string;
  actions: DJSEMFanLiveLinkAction[];
  setlist: DJSEMPublishedSetlist;
  consentConfig?: DJSEMFanConsentConfig;
  analyticsEnabled: boolean;
  warnings: string[];
  createdAt: string;
  updatedAt: string;
}

/** Input to {@link FanLiveLinkService.createPage}. */
export interface CreateFanLiveLinkInput {
  projectId?: string; slug: string; title: string;
  artistName: string; eventTitle?: string;
  pageMode: DJSEMFanPageMode; visibility: DJSEMFanPageVisibility;
  actions?: DJSEMFanLiveLinkAction[];
}

/** Creates and manages fan-facing QR/live-link pages, enforcing slug uniqueness. */
export class FanLiveLinkService {
  private slugs = new Set<string>();

  /**
   * Creates a new draft live-link page.
   * @param input - The slug, title, artist, mode, visibility, and optional actions.
   * @returns The new page in `"draft"` status.
   * @throws If the slug is already in use.
   */
  createPage(input: CreateFanLiveLinkInput): DJSEMFanLiveLinkPage {
    if (this.slugs.has(input.slug)) throw new Error(`Slug already in use: ${input.slug}`);
    this.slugs.add(input.slug);
    return {
      id: `fl_${_seq.next()}`, projectId: input.projectId,
      slug: input.slug, title: input.title,
      status: "draft", visibility: input.visibility,
      pageMode: input.pageMode, artistName: input.artistName,
      eventTitle: input.eventTitle,
      actions: input.actions ?? [],
      setlist: { enabled: false, publishTiming: "never", trackItems: [] },
      consentConfig: undefined, analyticsEnabled: false,
      warnings: [], createdAt: AT, updatedAt: AT,
    };
  }

  /**
   * Publishes a draft/paused page, making it active.
   * @param page - The page to publish.
   * @returns A new page with status `"active"`.
   */
  publishPage(page: DJSEMFanLiveLinkPage): DJSEMFanLiveLinkPage {
    return { ...page, status: "active", updatedAt: AT };
  }

  /**
   * Pauses an active page.
   * @param page - The page to pause.
   * @returns A new page with status `"paused"`.
   */
  pausePage(page: DJSEMFanLiveLinkPage): DJSEMFanLiveLinkPage {
    return { ...page, status: "paused", updatedAt: AT };
  }

  /**
   * Publishes the setlist for a page as an after-show reveal.
   * @param page - The page to update.
   * @param tracks - The setlist tracks to publish.
   * @returns A new page with the setlist enabled and `publishTiming: "after_show"`.
   */
  publishSetlistAfterShow(page: DJSEMFanLiveLinkPage, tracks: DJSEMPublishedSetlistTrack[]): DJSEMFanLiveLinkPage {
    return { ...page, setlist: { enabled: true, publishTiming: "after_show", trackItems: tracks }, updatedAt: AT };
  }

  /**
   * Determines whether a page is currently reachable by the public.
   * @param page - The page to check.
   * @returns True if the page is active and either public or unlisted.
   */
  isPubliclyAccessible(page: DJSEMFanLiveLinkPage): boolean {
    return page.status === "active" && (page.visibility === "public" || page.visibility === "unlisted");
  }

  /**
   * Determines whether a page requires fan consent before collecting an email.
   * @param page - The page to check.
   * @returns True if the page's consent config opts into email collection.
   */
  requiresConsent(page: DJSEMFanLiveLinkPage): boolean {
    return !!(page.consentConfig?.collectEmail);
  }
}

// ============================================================================
// PROMPT 50 — Mobile Companion / Remote Control
// ============================================================================

/** A remote-control permission that can be granted to a mobile companion session. */
export type DJSEMMobileControlPermission =
  | "view_set" | "view_notes" | "add_note" | "trigger_manual_cue"
  | "next_scene" | "previous_scene" | "blackout" | "safe_mode"
  | "mark_highlight" | "checklist_update";

/** The type of remote command a mobile companion device can submit. */
export type DJSEMMobileCommandType =
  | "trigger_manual_cue" | "next_scene" | "previous_scene"
  | "blackout" | "safe_mode" | "add_note" | "mark_highlight" | "checklist_update";

/** Lifecycle status of a submitted mobile control command. */
export type DJSEMMobileCommandStatus = "requested" | "accepted" | "rejected" | "executed" | "failed";

/** A mobile-companion remote-control pairing session for a live project. */
export interface DJSEMMobileCompanionSession {
  id: string;
  projectId?: string;
  pairingCodeHash?: string;
  status: "created" | "pairing" | "active" | "paused" | "ended" | "expired";
  allowedControls: DJSEMMobileControlPermission[];
  connectedDeviceIds: string[];
  expiresAt?: string;
  warnings: string[];
  createdAt: string;
  updatedAt: string;
}

/** A single remote-control command submitted from a paired mobile device. */
export interface DJSEMMobileControlCommand {
  id: string;
  sessionId: string;
  commandType: DJSEMMobileCommandType;
  payload: unknown;
  deviceId?: string;
  status: DJSEMMobileCommandStatus;
  rejectionReason?: string;
  createdAt: string;
}

/** A live note captured from a mobile companion device during a session. */
export interface DJSEMMobileLiveNote {
  id: string;
  sessionId: string;
  noteType: "general" | "track" | "cue" | "visual" | "technical" | "highlight";
  body: string;
  trackId?: string;
  timeMs?: number;
  createdAt: string;
}

/** Input to {@link MobileCompanionSessionService.createSession}. */
export interface CreateMobileSessionInput {
  projectId?: string;
  pairingCodeHash: string;
  allowedControls: DJSEMMobileControlPermission[];
  expiresAt?: string;
}

/** Creates and manages mobile companion pairing sessions and their device connections. */
export class MobileCompanionSessionService {
  /**
   * Creates a new mobile companion session in `"created"` status, awaiting pairing.
   * @param input - The (hashed) pairing code, allowed controls, and optional expiry.
   * @returns The new session.
   * @throws If `pairingCodeHash` is shorter than 16 characters.
   */
  createSession(input: CreateMobileSessionInput): DJSEMMobileCompanionSession {
    if (input.pairingCodeHash.length < 16) throw new Error("Pairing code hash must be >= 16 chars");
    return {
      id: `mcs_${_seq.next()}`, projectId: input.projectId,
      pairingCodeHash: input.pairingCodeHash,
      status: "created", allowedControls: input.allowedControls,
      connectedDeviceIds: [], expiresAt: input.expiresAt,
      warnings: [], createdAt: AT, updatedAt: AT,
    };
  }

  /**
   * Pairs a device to a session, activating it. No-op if the session has ended or expired.
   * @param session - The session to update.
   * @param deviceId - The device connecting.
   * @returns A new session with the device added and status `"active"`, or unchanged if ended/expired.
   */
  pairDevice(session: DJSEMMobileCompanionSession, deviceId: string): DJSEMMobileCompanionSession {
    if (session.status === "ended" || session.status === "expired") return session;
    return { ...session, status: "active", connectedDeviceIds: [...session.connectedDeviceIds, deviceId], updatedAt: AT };
  }

  /**
   * Revokes a paired device's connection.
   * @param session - The session to update.
   * @param deviceId - The device to revoke.
   * @returns A new session without the given device.
   */
  revokeDevice(session: DJSEMMobileCompanionSession, deviceId: string): DJSEMMobileCompanionSession {
    return { ...session, connectedDeviceIds: session.connectedDeviceIds.filter(d => d !== deviceId), updatedAt: AT };
  }

  /**
   * Ends a session.
   * @param session - The session to end.
   * @returns A new session with status `"ended"`.
   */
  endSession(session: DJSEMMobileCompanionSession): DJSEMMobileCompanionSession {
    return { ...session, status: "ended", updatedAt: AT };
  }
}

/** Validates and submits remote-control commands from paired mobile devices. */
export class MobileControlCommandService {
  /**
   * Validates whether a command may be submitted: session must be active, the device must
   * be paired, and the session must have been granted the command's required permission.
   * @param session - The session the command is submitted against.
   * @param commandType - The command being attempted.
   * @param deviceId - The device submitting the command.
   * @returns The validation result.
   */
  validateCommand(session: DJSEMMobileCompanionSession, commandType: DJSEMMobileCommandType, deviceId: string): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (session.status !== "active") issues.push({ path: "$.session", message: "session not active" });
    if (!session.connectedDeviceIds.includes(deviceId)) issues.push({ path: "$.deviceId", message: "device not paired or revoked" });
    // Map command to permission
    const permMap: Record<DJSEMMobileCommandType, DJSEMMobileControlPermission> = {
      trigger_manual_cue: "trigger_manual_cue", next_scene: "next_scene", previous_scene: "previous_scene",
      blackout: "blackout", safe_mode: "safe_mode", add_note: "add_note",
      mark_highlight: "mark_highlight", checklist_update: "checklist_update",
    };
    const needed = permMap[commandType];
    if (needed && !session.allowedControls.includes(needed)) issues.push({ path: "$.commandType", message: `permission ${needed} not granted` });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "mobile_command");
  }

  /**
   * Validates and records a remote-control command submission.
   * @param session - The session the command is submitted against.
   * @param commandType - The command being submitted.
   * @param deviceId - The device submitting the command.
   * @param payload - Optional command-specific payload.
   * @returns The new command record, with status `"accepted"` or `"rejected"` based on validation.
   */
  submitCommand(session: DJSEMMobileCompanionSession, commandType: DJSEMMobileCommandType, deviceId: string, payload?: unknown): DJSEMMobileControlCommand {
    const v = this.validateCommand(session, commandType, deviceId);
    return {
      id: `mcc_${_seq.next()}`, sessionId: session.id, commandType,
      payload: payload ?? {}, deviceId,
      status: v.ok ? "accepted" : "rejected",
      rejectionReason: v.ok ? undefined : "validation failed",
      createdAt: AT,
    };
  }
}

/** Records and lists bounded live notes captured from mobile companion devices. */
export class MobileLiveNotesService {
  private notes: DJSEMMobileLiveNote[] = [];
  // Bounded so a long live session cannot grow the note buffer without limit.
  constructor(private maxNotes = 5000) {}

  /**
   * Adds a new live note, dropping the oldest notes once `maxNotes` is exceeded.
   * @param sessionId - The mobile companion session the note belongs to.
   * @param noteType - The kind of note.
   * @param body - The note text.
   * @param opts - Optional associated track id and/or timestamp (ms).
   * @returns The new note record.
   */
  addNote(sessionId: string, noteType: DJSEMMobileLiveNote["noteType"], body: string, opts?: { trackId?: string; timeMs?: number }): DJSEMMobileLiveNote {
    const note: DJSEMMobileLiveNote = { id: `mln_${_seq.next()}`, sessionId, noteType, body, trackId: opts?.trackId, timeMs: opts?.timeMs, createdAt: AT };
    this.notes.push(note);
    if (this.notes.length > this.maxNotes) this.notes.splice(0, this.notes.length - this.maxNotes);
    return note;
  }

  /**
   * Lists notes for a session.
   * @param sessionId - The session to list notes for.
   * @returns The matching notes.
   */
  listNotes(sessionId: string): DJSEMMobileLiveNote[] {
    return this.notes.filter(n => n.sessionId === sessionId);
  }
}
